export type SeoConfig = {
    title: string;
    description: string;
    keywords: string;
    path: string;
};

const DEFAULT_SEO: SeoConfig = {
    title: "Centrum Cyberbezpieczeństwa WSIiZ",
    description: "Centrum Cyberbezpieczeństwa WSIiZ w Rzeszowie: edukacja, projekty, badania, praktyki i współpraca w obszarze bezpieczeństwa cyfrowego.",
    keywords: "cyberbezpieczeństwo, WSIiZ, Rzeszów, bezpieczeństwo cyfrowe, centrum cyberbezpieczeństwa",
    path: "/",
};

const ROUTE_SEO: SeoConfig[] = [
    DEFAULT_SEO,
    {
        title: "O nas | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Poznaj misję, zespół i zakres działalności Centrum Cyberbezpieczeństwa WSIiZ.",
        keywords: "o centrum, zespół cyberbezpieczeństwa, misja centrum, WSIiZ",
        path: "/o_centrum/o_nas",
    },
    {
        title: "Projekty | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Aktualne projekty badawcze, edukacyjne i wdrożeniowe Centrum Cyberbezpieczeństwa WSIiZ.",
        keywords: "projekty cyberbezpieczeństwo, badania, wdrożenia, edukacja cyfrowa",
        path: "/projekty/aktualne",
    },
    {
        title: "Kontakt | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Skontaktuj się z Centrum Cyberbezpieczeństwa WSIiZ w Rzeszowie.",
        keywords: "kontakt, Centrum Cyberbezpieczeństwa, WSIiZ, Rzeszów",
        path: "/kontakt",
    },
    {
        title: "Dla biznesu | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Współpraca z biznesem w zakresie analiz, projektów studenckich i podnoszenia bezpieczeństwa cyfrowego organizacji.",
        keywords: "cyberbezpieczeństwo dla biznesu, analiza ryzyka, bezpieczeństwo informacji",
        path: "/biznes",
    },
    {
        title: "Praktyki | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Praktyki studenckie i rozwój kompetencji w obszarze cyberbezpieczeństwa, IT oraz nowych technologii.",
        keywords: "praktyki cyberbezpieczeństwo, staże IT, praktyki WSIiZ",
        path: "/praktyki",
    },
    {
        title: "Oferta dla otoczenia | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Oferta współpracy dla instytucji, organizacji i środowiska społeczno-gospodarczego.",
        keywords: "współpraca, instytucje publiczne, szkolenia cyberbezpieczeństwo",
        path: "/otoczenie",
    },
    {
        title: "Koła naukowe | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Studenckie koła naukowe działające przy Centrum Cyberbezpieczeństwa WSIiZ.",
        keywords: "koła naukowe, studenci, AEGIS, CONECT, NODE",
        path: "/kola-naukowe",
    },
    {
        title: "Panel taktyczny | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Narzędzia analityczne do weryfikacji haseł, wycieków danych i bezpieczeństwa cyfrowego.",
        keywords: "panel taktyczny, sprawdzanie haseł, wycieki danych, OSINT",
        path: "/panel-taktyczny",
    },
    {
        title: "Aktualności | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Najnowsze informacje, wydarzenia i komunikaty Centrum Cyberbezpieczeństwa WSIiZ.",
        keywords: "aktualności cyberbezpieczeństwo, wydarzenia, komunikaty",
        path: "/aktualnosci",
    },
    {
        title: "Kalendarz wydarzeń | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Harmonogram spotkań, szkoleń, warsztatów i wydarzeń Centrum Cyberbezpieczeństwa.",
        keywords: "kalendarz wydarzeń, szkolenia, warsztaty cyberbezpieczeństwo",
        path: "/student/kalendarz",
    },
    {
        title: "Wasze propozycje | Centrum Cyberbezpieczeństwa WSIiZ",
        description: "Zgłaszanie tematów prac dyplomowych, pomysłów i projektów studenckich.",
        keywords: "propozycje studentów, prace dyplomowe, projekty studenckie",
        path: "/student/propozycje",
    },
];

export const getSeoConfig = (pathname: string): SeoConfig => {
    const normalizedPath = pathname.replace(/\/$/, "") || "/";

    if (normalizedPath.startsWith("/aktualnosci/")) {
        return {
            title: "Aktualność | Centrum Cyberbezpieczeństwa WSIiZ",
            description: "Szczegóły aktualności Centrum Cyberbezpieczeństwa WSIiZ.",
            keywords: "aktualność, cyberbezpieczeństwo, WSIiZ",
            path: normalizedPath,
        };
    }

    if (normalizedPath.startsWith("/projekty/")) {
        return ROUTE_SEO.find((item) => item.path === "/projekty/aktualne") || DEFAULT_SEO;
    }

    return ROUTE_SEO.find((item) => item.path === normalizedPath) || DEFAULT_SEO;
};

const upsertMeta = (selector: string, createAttrs: Record<string, string>, content: string) => {
    let element = document.head.querySelector<HTMLMetaElement>(selector);

    if (!element) {
        element = document.createElement("meta");
        Object.entries(createAttrs).forEach(([key, value]) => element?.setAttribute(key, value));
        document.head.appendChild(element);
    }

    element.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
    let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }

    element.setAttribute("href", href);
};

const upsertJsonLd = (id: string, data: object) => {
    let element = document.head.querySelector<HTMLScriptElement>(`script#${id}`);

    if (!element) {
        element = document.createElement("script");
        element.id = id;
        element.type = "application/ld+json";
        document.head.appendChild(element);
    }

    element.textContent = JSON.stringify(data);
};

export const applySeo = (config: SeoConfig) => {
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${config.path}`;

    document.documentElement.lang = "pl";
    document.title = config.title;

    upsertMeta('meta[name="description"]', { name: "description" }, config.description);
    upsertMeta('meta[name="keywords"]', { name: "keywords" }, config.keywords);
    upsertMeta('meta[name="robots"]', { name: "robots" }, "index,follow,max-image-preview:large");
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, config.title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, config.description);
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    upsertMeta('meta[property="og:locale"]', { property: "og:locale" }, "pl_PL");
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, config.title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, config.description);
    upsertLink("canonical", canonicalUrl);

    upsertJsonLd("organization-jsonld", {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: "Centrum Cyberbezpieczeństwa WSIiZ",
        url: origin,
        address: {
            "@type": "PostalAddress",
            streetAddress: "ul. mjr. Henryka Sucharskiego 2",
            postalCode: "35-225",
            addressLocality: "Rzeszów",
            addressCountry: "PL",
        },
        email: "cc@wsiz.edu.pl",
    });

    upsertJsonLd("website-jsonld", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Centrum Cyberbezpieczeństwa WSIiZ",
        url: origin,
        inLanguage: "pl-PL",
    });
};
